import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.6, ease: "easeOut" } }),
};

const CATEGORIES = ["All", "Weddings", "Corporate", "Yacht & Sea", "Drone", "Nightlife"];

const PORTFOLIO = [
  { id: 1, src: "https://images.unsplash.com/photo-1606030688449-d23c963c19ce?w=800&q=80", cat: "Yacht & Sea", title: "Montego Bay Yacht Gala" },
  { id: 2, src: "https://images.unsplash.com/photo-1661822951996-fbb0f7a6face?w=800&q=80", cat: "Yacht & Sea", title: "Sunset Soiree at Sea" },
  { id: 3, src: "https://images.unsplash.com/photo-1559385301-0187cb6eff46?w=800&q=80", cat: "Yacht & Sea", title: "Royal Caribbean Reception" },
  { id: 4, src: "https://images.unsplash.com/photo-1641900833685-2874c5ebd974?w=800&q=80", cat: "Drone", title: "Aerial Paradise Cove" },
  { id: 5, src: "https://images.unsplash.com/photo-1775036191163-a3fbb14a1370?w=800&q=80", cat: "Nightlife", title: "VIP Kingston Nights" },
  { id: 6, src: "https://images.unsplash.com/photo-1561721632-a0d70ac1d15b?w=800&q=80", cat: "Corporate", title: "Sterling Enterprises Annual" },
  { id: 7, src: "https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80", cat: "Weddings", title: "The Sterling-Chen Wedding" },
  { id: 8, src: "https://images.unsplash.com/photo-1545232979-8bf68ee9b1af?w=800&q=80", cat: "Weddings", title: "Oceanview Ceremony" },
  { id: 9, src: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80", cat: "Corporate", title: "Tech Summit Kingston" },
  { id: 10, src: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800&q=80", cat: "Nightlife", title: "Exclusive Launch Party" },
  { id: 11, src: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80", cat: "Drone", title: "Coastal Villa Aerial" },
  { id: 12, src: "https://images.unsplash.com/photo-1544078751-58fee2d8a03b?w=800&q=80", cat: "Yacht & Sea", title: "Private Yacht Celebration" },
];

export default function PortfolioPage() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const filtered = filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.cat === filter);

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);
  const nextImage = () => setLightbox((prev) => (prev + 1) % filtered.length);
  const prevImage = () => setLightbox((prev) => (prev - 1 + filtered.length) % filtered.length);

  return (
    <div data-testid="portfolio-page" className="pt-20">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-luxury-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">Portfolio</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-8">
              A Curated Collection of<br /><span className="gold-gradient-text">Excellence</span>
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-luxury-charcoal py-8 border-y border-white/5 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap gap-3">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(cat)}
              className={`px-5 py-2 text-sm font-body font-light transition-all ${
                filter === cat
                  ? "bg-gold text-black"
                  : "border border-white/10 text-white/60 hover:border-white/30 hover:text-white"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section className="py-12 bg-luxury-charcoal">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div layout className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 sm:gap-1.5">
            <AnimatePresence mode="popLayout">
              {filtered.map((item, i) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="relative group cursor-pointer overflow-hidden aspect-square bg-luxury-surface"
                  onClick={() => openLightbox(i)}
                  data-testid={`portfolio-item-${item.id}`}
                >
                  <img src={item.src} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                  {/* Instagram-style: subtle overlay only on hover, no gradient fade */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center pointer-events-none">
                    <span className="text-gold text-[10px] font-body uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-200">{item.cat}</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lightbox-overlay"
            onClick={closeLightbox}
            data-testid="lightbox-overlay"
          >
            <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="absolute top-6 right-6 text-white/60 hover:text-white z-10" data-testid="lightbox-close">
              <X size={28} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-6 top-1/2 -translate-y-1/2 text-white/60 hover:text-white z-10" data-testid="lightbox-prev">
              <ChevronLeft size={36} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-6 top-1/2 -translate-y-1/2 text-white/60 hover:text-white z-10" data-testid="lightbox-next">
              <ChevronRight size={36} />
            </button>
            <div onClick={(e) => e.stopPropagation()} className="max-w-5xl max-h-[85vh] px-4">
              <img src={filtered[lightbox]?.src} alt={filtered[lightbox]?.title} className="max-w-full max-h-[80vh] object-contain" />
              <div className="text-center mt-4">
                <p className="text-gold text-xs font-body uppercase tracking-wider mb-1">{filtered[lightbox]?.cat}</p>
                <p className="text-white text-sm font-body font-light">{filtered[lightbox]?.title}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
